// Custom hook for managing image uploads in InstaWorthy
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { 
    uploadImageBatch, 
    saveUploadSession, 
    updateTrainingStatus,
    cleanupFailedUploads,
    getLatestUploadSession
} from '../utils/storageService';
import { validateImage, createPreviewUrl, revokePreviewUrl } from '../utils/imageUtils';
import { config } from '../Constants';

export const useImageUpload = () => {
    const { currentUser } = useAuth();
    const { updateTrainingStatus: updateUserTrainingStatus } = useUser();
    
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load existing uploaded images on mount
    useEffect(() => {
        const loadExistingImages = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }

            try {
                console.log('🔍 Loading existing images for user:', currentUser.uid);
                const latestSession = await getLatestUploadSession(currentUser.uid);
                console.log('📁 Latest session found:', latestSession);
                
                if (latestSession && latestSession.images && latestSession.images.length > 0) {
                    console.log('📷 Found images in session:', latestSession.images);
                    
                    // Convert Firebase storage images to our expected format
                    const existingImages = latestSession.images.map((image, index) => ({
                        id: `existing_${latestSession.id}_${index}`,
                        file: null, // No file object for existing images
                        preview: image.downloadURL, // Use download URL as preview
                        name: image.originalName || `Image ${index + 1}`,
                        size: image.size || 0,
                        status: 'uploaded', // Already uploaded
                        uploadProgress: 100,
                        error: null,
                        downloadURL: image.downloadURL,
                        uploadedAt: image.uploadedAt,
                        isExisting: true // Flag to identify existing images
                    }));
                    
                    console.log('✅ Setting uploaded images:', existingImages);
                    setUploadedImages(existingImages);
                } else {
                    console.log('❌ No images found in latest session');
                }
            } catch (error) {
                console.error('Error loading existing images:', error);
                setErrors(prev => [...prev, {
                    fileName: 'System',
                    errors: ['Failed to load existing images']
                }]);
            } finally {
                setLoading(false);
            }
        };

        loadExistingImages();
    }, [currentUser]);

    // Add images to upload queue
    const addImages = useCallback((files) => {
        const newImages = [];
        const newErrors = [];
        
        // Check if we have room for more images
        const remainingSlots = config.maxSelfies - uploadedImages.length;
        const filesToProcess = files.slice(0, remainingSlots);
        
        filesToProcess.forEach((file, index) => {
            const validation = validateImage(file);
            
            if (validation.isValid) {
                const imageData = {
                    id: `${Date.now()}_${index}`,
                    file,
                    preview: createPreviewUrl(file),
                    name: file.name,
                    size: file.size,
                    status: 'pending', // pending, uploading, uploaded, error
                    uploadProgress: 0,
                    error: null,
                    downloadURL: null
                };
                
                newImages.push(imageData);
            } else {
                newErrors.push({
                    fileName: file.name,
                    errors: validation.errors
                });
            }
        });
        
        setUploadedImages(prev => [...prev, ...newImages]);
        setErrors(prev => [...prev, ...newErrors]);
        
        return newImages.length;
    }, [uploadedImages.length]);

    // Remove image from upload queue
    const removeImage = useCallback((imageId) => {
        setUploadedImages(prev => {
            const imageToRemove = prev.find(img => img.id === imageId);
            // Only revoke URL if it's a local preview URL (not existing Firebase images)
            if (imageToRemove && imageToRemove.preview && !imageToRemove.isExisting) {
                revokePreviewUrl(imageToRemove.preview);
            }
            return prev.filter(img => img.id !== imageId);
        });
        
        // Remove from progress tracking
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[imageId];
            return newProgress;
        });
    }, []);

    // Upload all pending images
    const uploadImages = useCallback(async () => {
        if (!currentUser || uploading) return null;
        
        const pendingImages = uploadedImages.filter(img => img.status === 'pending');
        if (pendingImages.length === 0) return null;
        
        setUploading(true);
        setErrors([]);
        
        try {
            // Update status to uploading
            const imageIds = pendingImages.map(img => img.id);
            setUploadedImages(prev => prev.map(img => 
                imageIds.includes(img.id) 
                    ? { ...img, status: 'uploading' }
                    : img
            ));
            
            // Prepare files for upload
            const filesToUpload = pendingImages.map(img => img.file);
            
            // Upload images
            const uploadResults = await uploadImageBatch(
                filesToUpload,
                currentUser.uid,
                // Progress callback
                (fileIndex, progress, totalProgress) => {
                    const imageId = pendingImages[fileIndex].id;
                    setUploadProgress(prev => ({
                        ...prev,
                        [imageId]: progress
                    }));
                },
                // Complete callback
                (fileIndex, result) => {
                    const imageId = pendingImages[fileIndex].id;
                    setUploadedImages(prev => prev.map(img => 
                        img.id === imageId 
                            ? { 
                                ...img, 
                                status: 'uploaded',
                                downloadURL: result.downloadURL,
                                uploadedAt: result.uploadedAt
                            }
                            : img
                    ));
                },
                // Error callback
                (fileIndex, error) => {
                    const imageId = pendingImages[fileIndex].id;
                    setUploadedImages(prev => prev.map(img => 
                        img.id === imageId 
                            ? { 
                                ...img, 
                                status: 'error',
                                error: error.message
                            }
                            : img
                    ));
                }
            );
            
            // Save upload session to Firestore
            const sessionId = await saveUploadSession(currentUser.uid, uploadResults);
            
            // Update user training status
            await updateTrainingStatus(currentUser.uid, 'uploaded', {
                sessionId,
                imageCount: uploadResults.filter(r => !r.error).length,
                uploadedAt: new Date()
            });
            
            // Update user context
            await updateUserTrainingStatus('uploaded');
            
            // Clean up any failed uploads
            await cleanupFailedUploads(uploadResults);
            
            return {
                sessionId,
                results: uploadResults,
                successCount: uploadResults.filter(r => !r.error).length,
                totalCount: uploadResults.length
            };
            
        } catch (error) {
            console.error('Upload batch error:', error);
            setErrors(prev => [...prev, {
                fileName: 'Batch Upload',
                errors: [error.message]
            }]);
            return null;
        } finally {
            setUploading(false);
        }
    }, [currentUser, uploading, uploadedImages, updateUserTrainingStatus]);

    // Clear all images and reset state
    const clearImages = useCallback(() => {
        // Clean up preview URLs (only for non-existing images)
        uploadedImages.forEach(img => {
            if (img.preview && !img.isExisting) {
                revokePreviewUrl(img.preview);
            }
        });
        
        setUploadedImages([]);
        setUploadProgress({});
        setErrors([]);
    }, [uploadedImages]);

    // Clear errors
    const clearErrors = useCallback(() => {
        setErrors([]);
    }, []);

    // Calculate upload statistics
    const stats = {
        total: uploadedImages.length,
        pending: uploadedImages.filter(img => img.status === 'pending').length,
        uploading: uploadedImages.filter(img => img.status === 'uploading').length,
        uploaded: uploadedImages.filter(img => img.status === 'uploaded').length,
        errors: uploadedImages.filter(img => img.status === 'error').length,
        progress: Math.round((uploadedImages.length / config.maxSelfies) * 100),
        isComplete: uploadedImages.length >= config.maxSelfies,
        canUpload: uploadedImages.length < config.maxSelfies && !uploading
    };

    return {
        // State
        uploadedImages,
        uploading,
        uploadProgress,
        errors,
        stats,
        loading,
        
        // Actions
        addImages,
        removeImage,
        uploadImages,
        clearImages,
        clearErrors
    };
};